import React, { useState, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCloudArrowUp,
  faArrowLeft,
} from "@fortawesome/sharp-light-svg-icons";
import { faCircle } from "@fortawesome/sharp-solid-svg-icons";
import Modal from "components/Modal/Modal";
import Button from "components/core/Button/Button";
import styles from "./ManualUploadModal.module.scss";
import BrokerageConnectMessage from "components/BrokerageConnect/BrokerageConnectMessage/BrokerageConnectMessage";
import BrokerageConnectIssue from "components/BrokerageConnect/BrokerageConnectIssue/BrokerageConnectIssue";
import { ErrorType } from "components/BrokerageConnect/BrokerageConnectIssue/BrokerageConnectIssue";
import { fetchSecretFromApi } from "source/secureSecrets";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useMutationBundle, useFetchUser } from "source/hooks";
import { SaveMobileAnalysisResult } from "$gql/mutations/general/SaveMobileAnalysisResult.gen";
import { SubmitManualUpload } from "$gql/mutations/general/SubmitManualUpload.gen";
import { UpdateManualInfoApproval } from "$gql/mutations/general/UpdateManualInfoApproval.gen";
import { useUser } from "components/UserContext/UserContext";
import { getDevOverrides, isLocalhost } from "source/utils/devUtils";

const MySwal = withReactContent(Swal);

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const SECRET_NAME = "tii-environment";

type Props = {
  visible: boolean;
  onClose: () => void;
};

type FileStatus = "pending" | "success" | "error";

type FileWithStatus = {
  name: string;
  size: number;
  type: string;
  uploadStatus: FileStatus;
  errorType?: ErrorType;
  jobId?: string;
  file: File; // Add the original File object
};

// Add this interface to define the shape of our secrets
interface SecretData {
  DEV_AI_API_ENDPOINT: string;
  DEV_AI_API_ENDPOINT_KEY: string;
  PROD_AI_API_ENDPOINT: string;
  PROD_AI_API_ENDPOINT_KEY: string;
}

const getErrorTypeFromCode = (errorCode: string): ErrorType => {
  // General service errors
  const generalServiceErrors = [
    "DOC_PARSE_1001",
    "DOC_PARSE_1002",
    "DOC_PARSE_1003",
    "DOC_PARSE_1005",
    "DOC_PARSE_1006",
    "DOC_PARSE_1007",
    "DOC_PARSE_1008",
    "DOC_PARSE_1011",
    "PDF2IMG_2001",
    "PDF2IMG_2002",
    "PDF2IMG_2003",
    "FRAUD_3001",
    "FRAUD_3002",
    "FRAUD_3004",
    "FRAUD_3005",
    "FRAUD_3006",
    "GENERIC_0000",
  ];

  const knownErrors = ["FRAUD_3003"];

  // Wrong document upload errors
  const wrongDocumentErrors = ["DOC_PARSE_1004", "DOC_PARSE_1012"];

  // Wrong name errors
  const wrongNameErrors = ["DOC_PARSE_1013", "DOC_PARSE_1014"];

  // Fraud detection errors
  const fraudErrors = ["DOC_PARSE_1009", "DOC_PARSE_1010"];

  if (generalServiceErrors.includes(errorCode)) {
    return "upload";
  }

  if (wrongDocumentErrors.includes(errorCode)) {
    return "config";
  }

  if (wrongNameErrors.includes(errorCode)) {
    return "name";
  }

  if (fraudErrors.includes(errorCode)) {
    return "fraud";
  }

  if (knownErrors.includes(errorCode)) {
    return "known";
  }

  return "default";
};

const ProcessingProgress = () => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    "Uploading Documents",
    "Processing Files",
    "Analyzing Statements",
    "Validating Data",
    "Finishing Up",
  ];

  useEffect(() => {
    const totalDuration = 25000; // 15 seconds
    const intervalTime = 50; // Update every 50ms for smooth animation
    const incrementPerInterval = (100 * intervalTime) / totalDuration;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = Math.min(prev + incrementPerInterval, 99); // Cap at 99% until complete
        // Update step text every 20%
        const newStep = Math.floor((newProgress / 100) * (steps.length - 1));
        if (newStep !== currentStep) {
          setCurrentStep(newStep);
        }
        return newProgress;
      });
    }, intervalTime);

    return () => clearInterval(progressInterval);
  }, []);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 600,
          margin: "0 auto",
          padding: "40px",
          background: "rgba(255, 255, 255, 0.9)",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "16px",
            backgroundColor: "#f0f0f0",
            borderRadius: "4px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: `linear-gradient(45deg, #85bb65, #83a270, #8cce65)`,
              backgroundSize: "200% 200%",
              animation: "gradient 2s ease infinite",
              position: "relative",
              transition: "width 50ms linear",
              borderRadius: "4px",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 10px,
                  rgba(255,255,255,0.1) 10px,
                  rgba(255,255,255,0.1) 20px
                )`,
                animation: "move 1s linear infinite",
              }}
            />
          </div>
        </div>
        <div
          style={{
            textAlign: "center",
            marginTop: "36px",
            fontSize: "16px",
            fontWeight: 500,
            color: "#091c37",
            transition: "all 0.3s ease",
            minHeight: "20px",
            lineHeight: "1.4",
          }}
        >
          {steps[currentStep]}
        </div>
        <style>
          {`
            @keyframes gradient {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            @keyframes move {
              0% { background-position: 0 0; }
              100% { background-position: 28px 0; }
            }
          `}
        </style>
      </div>
    </div>
  );
};

const ManualUploadModal = (props: Props) => {
  const userContext = useUser();
  const userBundle = useFetchUser();
  const [file, setFile] = useState<FileWithStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showStatementIssues, setShowStatementIssues] = useState(false);
  const [errorType, setErrorType] = useState<ErrorType | undefined>();
  const [apiEndpoint, setApiEndpoint] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const getApiKey = async (): Promise<string> => {
    const devOverrides = getDevOverrides();

    if (isLocalhost() && devOverrides.manualUploadApiKey) {
      return devOverrides.manualUploadApiKey;
    }

    const result = await fetchSecretFromApi(SECRET_NAME);
    if (!result.data) throw new Error("Failed to fetch API key");

    const isSecretData = (data: any): data is SecretData => {
      return (
        typeof data === "object" &&
        data !== null &&
        "DEV_AI_API_ENDPOINT_KEY" in data &&
        "PROD_AI_API_ENDPOINT_KEY" in data
      );
    };

    if (!isSecretData(result.data)) {
      throw new Error("Invalid secret data format");
    }

    const hostname = window.location.hostname;
    const isProduction =
      hostname === "tiicker.com" || hostname === "staging.tiicker.com";

    return isProduction
      ? result.data.PROD_AI_API_ENDPOINT_KEY
      : result.data.DEV_AI_API_ENDPOINT_KEY;
  };

  const [saveMobileAnalysisResult] = useMutationBundle(
    SaveMobileAnalysisResult
  );
  const [submitManualUpload] = useMutationBundle(SubmitManualUpload);
  const [updateManualInfoApproval] = useMutationBundle(
    UpdateManualInfoApproval
  );

  useEffect(() => {
    const setupEndpoint = async () => {
      const devOverrides = getDevOverrides();
      const hostname = window.location.hostname;

      if (isLocalhost() && devOverrides.manualUploadApiEndpoint) {
        setApiEndpoint(`${devOverrides.manualUploadApiEndpoint}/analysis`);
        return;
      }

      const result = await fetchSecretFromApi(SECRET_NAME);
      if (!result.data) return;

      const isSecretData = (data: any): data is SecretData => {
        return (
          typeof data === "object" &&
          data !== null &&
          "DEV_AI_API_ENDPOINT" in data &&
          "PROD_AI_API_ENDPOINT" in data
        );
      };

      if (isSecretData(result.data)) {
        const isProduction =
          hostname === "tiicker.com" || hostname === "staging.tiicker.com";
        setApiEndpoint(
          isProduction
            ? `${result.data.PROD_AI_API_ENDPOINT}/analysis`
            : `${result.data.DEV_AI_API_ENDPOINT}/analysis`
        );
      }
    };

    setupEndpoint();
  }, []);

  const uploadFile = async (file: FileWithStatus): Promise<string | null> => {
    try {
      const apiKey = await getApiKey();
      const formData = new FormData();
      formData.append("file", file.file, file.name);

      const submitterName = userBundle.user
        ? `${userBundle.user.firstName} ${userBundle.user.lastName}`
        : `Unknown_User_${Date.now()}`;
      formData.append("submitterName", submitterName);
      formData.append("skipHashCheck", isLocalhost().toString());

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "x-api-key": apiKey,
        },
        body: formData,
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }

      const responseData = await response.json();

      setFile((prev) =>
        prev
          ? {
              ...prev,
              uploadStatus: "pending" as FileStatus,
              jobId: responseData.jobId,
            }
          : null
      );

      return responseData.jobId;
    } catch (error) {
      console.error("❌ Upload failed:", error);
      setFile((prev) =>
        prev
          ? {
              ...prev,
              uploadStatus: "error" as FileStatus,
            }
          : null
      );
      return null;
    }
  };

  const checkJobStatus = async (jobId: string): Promise<any> => {
    try {
      const apiKey = await getApiKey();
      const response = await fetch(`${apiEndpoint}/${jobId}`, {
        headers: { "x-api-key": apiKey },
        mode: "cors", // Keep this
        credentials: "omit", // Explicitly omit credentials since we're using '*' for CORS
      });

      if (!response.ok) {
        throw new Error(`Status check failed: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("❌ Status check failed:", error);
      throw error;
    }
  };

  const checkForDuplicate = async (result: any) => {
    if (result.isNearDuplicate) {
      const isDuplicate = result.isNearDuplicate.isDuplicate;
      const collisionRatio = result.isNearDuplicate.collisionRatio;

      if (collisionRatio === "SKIPPED") {
        return false;
      }

      // Convert collision ratio (e.g., "5/8") to a number
      const [numerator, denominator] = collisionRatio.split("/").map(Number);
      const ratio = numerator / denominator;

      if (isDuplicate || ratio >= 5 / 8) {
        setErrorType("duplicate");
        setShowStatementIssues(true);
        setFile((prev) =>
          prev ? { ...prev, uploadStatus: "error" as FileStatus } : null
        );
        return true;
      }
    }

    return false;
  };

  const processJobResult = async (jobId: string, result: any) => {
    try {
      // Handle failed status
      if (result.status === "FAILED" && result.result?.errorCode) {
        const errorCategory = getErrorTypeFromCode(result.result.errorCode);
        setErrorType(errorCategory);
        setShowStatementIssues(true);
        setFile((prev) =>
          prev ? { ...prev, uploadStatus: "error" as FileStatus } : null
        );
        return;
      }

      // Check for duplicate documents
      const isDuplicate = await checkForDuplicate(result);

      const statementDate = new Date(result.stockInfo.date);
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      const isExpired = statementDate <= sixtyDaysAgo;

      if (!isExpired && !isDuplicate) {
        await saveMobileAnalysisResult({
          variables: {
            results: JSON.stringify(result),
            jobId: jobId,
          },
        });
      }

      const submitResponse = await submitManualUpload({
        variables: {
          userId: userContext.state.userId || 0,
          jobId: jobId,
          s3ImageBucket: result.s3ImageBucket,
          s3DocumentBucket: result.s3DocumentBucket,
          s3ImageKey: result.s3ImageKey,
          s3DocumentKey: result.s3DocumentKey,
          status: result.status,
          creationDate: result.timestamp,
        },
      });

      if (submitResponse) {
        if (isExpired || isDuplicate) {
          await updateManualInfoApproval({
            variables: {
              jobId: jobId,
              needsApproval: true,
              approved: false,
            },
          });

          if (isExpired) {
            setFile((prev) =>
              prev ? { ...prev, uploadStatus: "error" as FileStatus } : null
            );
            setErrorType("expired");
            setShowStatementIssues(true);
          }
        } else {
          await updateManualInfoApproval({
            variables: {
              jobId: jobId,
              needsApproval: false,
              approved: true,
            },
          });
          setFile((prev) =>
            prev ? { ...prev, uploadStatus: "success" as FileStatus } : null
          );
        }
      }
    } catch (error) {
      console.error("❌ Error processing job result:", error);
      throw error;
    }
  };

  const pollJobStatus = async (jobId: string): Promise<boolean> => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes at 5-second intervals

    while (attempts < maxAttempts) {
      try {
        const result = await checkJobStatus(jobId);

        if (result.status === "COMPLETE") {
          setFile((prev) =>
            prev ? { ...prev, uploadStatus: "success" as FileStatus } : null
          );
          return true;
        }

        if (result.status === "FAILED") {
          setFile((prev) =>
            prev ? { ...prev, uploadStatus: "error" as FileStatus } : null
          );
          return false;
        }

        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 5000));
      } catch (error) {
        console.error("❌ Polling error:", error);
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
    }

    setFile((prev) =>
      prev ? { ...prev, uploadStatus: "error" as FileStatus } : null
    );
    return false;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setIsProcessing(true);
    setShowStatementIssues(false);

    try {
      if (!file) {
        throw new Error("No file to upload");
      }

      // Upload file and get jobId
      const jobId = await uploadFile(file);

      if (!jobId) {
        throw new Error("Upload failed");
      }

      // Start polling for job
      const pollingResult = await pollJobStatus(jobId);

      if (pollingResult) {
        try {
          const jobResult = await checkJobStatus(jobId);
          await processJobResult(jobId, jobResult);
        } catch (error) {
          console.error(`❌ Error processing job ${jobId}:`, error);
          setFile((prev) =>
            prev ? { ...prev, uploadStatus: "error" as FileStatus } : null
          );
        }
      }
    } catch (error) {
      console.error("❌ Submission error:", error);
      setErrorType("upload");
      setShowStatementIssues(true);
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  const validateFileSize = (file: File): boolean => {
    return file.size <= MAX_FILE_SIZE;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setShowStatementIssues(false);

    const droppedFile = Array.from(e.dataTransfer.files).find(
      (file): file is File => file.type === "application/pdf"
    );

    if (!droppedFile) {
      return;
    }

    if (!validateFileSize(droppedFile)) {
      setErrorType("size");
      setShowStatementIssues(true);
      return;
    }

    const fileWithStatus: FileWithStatus = {
      name: droppedFile.name,
      size: droppedFile.size,
      type: droppedFile.type,
      uploadStatus: "pending",
      file: droppedFile,
    };

    setFile(fileWithStatus);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowStatementIssues(false);

    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      if (!validateFileSize(selectedFile)) {
        setErrorType("size");
        setShowStatementIssues(true);
        return;
      }

      const fileWithStatus: FileWithStatus = {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        uploadStatus: "pending",
        file: selectedFile,
      };

      setFile(fileWithStatus);
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const formatFileSize = (bytes: number): string => {
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${Math.round(size * 10) / 10} ${units[unitIndex]}`;
  };

  const getStatusColor = (status: FileStatus) => {
    switch (status) {
      case "success":
        return "#85BB65"; // Green
      case "error":
        return "#EF4444"; // Red
      default:
        return "#6B7280"; // Grey
    }
  };

  return (
    <Modal
      visible={props.visible}
      onClose={props.onClose}
      fullWidthSmall
      modalClassName={styles.ManualUploadModal}
      blockOutsideClick={true}
    >
      <div style={{ position: "relative" }}>
        <div className={styles.ManualUploadModal__content}>
          <div className={styles.ManualUploadModal__container}>
            {/* Left Column */}
            <div className={styles.ManualUploadModal__column}>
              <Button type="inline" noAnimate onClick={props.onClose}>
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className={styles.ManualUploadModal__arrowLeftIcon}
                />
                Back to Perk
              </Button>
              <h2 className={styles.ManualUploadModal__title}>
                Upload Brokerage Statement
              </h2>
              <h3 className={styles.ManualUploadModal__subtitle}>
                The following information must be included on all statements:
              </h3>

              <ul className={styles.ManualUploadModal__list}>
                <li className={styles.ManualUploadModal__listItem}>
                  Full name
                </li>
                <li className={styles.ManualUploadModal__listItem}>
                  Brokerage name
                </li>
                <li className={styles.ManualUploadModal__listItem}>
                  Date of Statement
                </li>
                <li className={styles.ManualUploadModal__listItem}>
                  Stocks held
                </li>
                <li className={styles.ManualUploadModal__listItem}>
                  Number of shares held
                </li>
                <li className={styles.ManualUploadModal__listItem}>
                  Value of portfolio
                </li>
              </ul>

              <p className={styles.ManualUploadModal__text}>
                Please note: Validation may be required again in 30 days if the
                perk has a holding period requirement
              </p>

              <BrokerageConnectMessage />
            </div>

            {/* Right Column */}
            <div className={styles.ManualUploadModal__column}>
              <div
                className={`${styles.ManualUploadModal__dropzone} ${
                  isDragging
                    ? styles["ManualUploadModal__dropzone--active"]
                    : ""
                }`}
                onDragEnter={handleDragEnter}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <FontAwesomeIcon
                  icon={faCloudArrowUp}
                  className={styles.ManualUploadModal__dropzone__icon}
                />
                <div className={styles.ManualUploadModal__dropzone__textBlock}>
                  <p className={styles.ManualUploadModal__dropzone__text}>
                    Drag & Drop
                  </p>
                  <p className={styles.ManualUploadModal__dropzone__subtext}>
                    or
                  </p>
                  <label className={styles.ManualUploadModal__fileLabel}>
                    Browse Files
                    <input
                      type="file"
                      accept=".pdf"
                      className={styles.ManualUploadModal__fileInput}
                      onChange={handleFileInput}
                    />
                  </label>
                </div>
                <p className={styles.ManualUploadModal__dropzone__sizeText}>
                  [.pdf files only, up to 5MB]
                </p>
              </div>

              <div className={styles.ManualUploadModal__fileList}>
                {file && (
                  <div className={styles.ManualUploadModal__fileItem}>
                    <div className={styles.ManualUploadModal__fileInfo}>
                      <FontAwesomeIcon
                        // @ts-ignore
                        icon={faCircle}
                        className={styles.ManualUploadModal__fileIcon}
                        style={{ color: getStatusColor(file.uploadStatus) }}
                      />
                      <span className={styles.ManualUploadModal__fileName}>
                        {file.name}
                      </span>
                    </div>
                    <div className={styles.ManualUploadModal__fileActions}>
                      <span className={styles.ManualUploadModal__fileSize}>
                        {formatFileSize(file.size)}
                      </span>
                      <button
                        className={styles.ManualUploadModal__fileRemove}
                        onClick={removeFile}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {showStatementIssues && (
                <BrokerageConnectIssue
                  errorType={errorType}
                  closeTitle={() => setShowStatementIssues(false)}
                  visible={showStatementIssues}
                />
              )}

              <Button
                onClick={handleSubmit}
                type="primary"
                disabled={file === null || isLoading}
              >
                {isLoading ? "Uploading..." : "Submit"}
              </Button>
            </div>
          </div>
        </div>
        {/* Processing overlay */}
        {isProcessing && <ProcessingProgress />}
      </div>
    </Modal>
  );
};

export default ManualUploadModal;
